import { MLEResult } from '@/utils/weibullMLE';
import { WeibullParameters } from '@/utils/weibull';
import { ForecastResult, TurbineForForecast as TurbineForForecastData } from '@/types/forecast';
import { createPeriodRangeLabel } from '@/utils/forecastUtils';

const MOCK_FAILURE_RATE = 0.1;
const MOCK_VARIANCE_FACTOR = 0.5;
const CONFIDENCE_INTERVAL_FACTOR = 0.2;

// Mock forecast engine for development/testing - uses simplified failure probability
class SimpleWeibullForecastEngine {
  static async calculateForecast(
    turbines: TurbineForForecastData[],
    weibullParams: WeibullParameters,
    horizonYears: number,
    granularityMonths: number,
    useMonteCarloSimulation: boolean,
    _monteCarloOptions?: {
      minSimulations?: number;
      maxSimulations?: number;
      convergenceThreshold?: number;
    },
    _mleResult?: MLEResult
  ): Promise<ForecastResult[]> {
    const totalMonths = horizonYears * 12;
    const periodsCount = Math.ceil(totalMonths / granularityMonths);
    const results: ForecastResult[] = [];

    let cumulativeTotal = 0;
    const startDate = new Date();

    for (let periodIndex = 0; periodIndex < periodsCount; periodIndex++) {
      const periodStartMonth = periodIndex * granularityMonths;
      const periodDate = new Date(startDate);
      periodDate.setMonth(periodDate.getMonth() + periodStartMonth);

      const periodFailures = turbines.length * MOCK_FAILURE_RATE * (1 + Math.random() * MOCK_VARIANCE_FACTOR);
      cumulativeTotal += periodFailures;

      const result: ForecastResult = {
        timePeriod: createPeriodRangeLabel(periodDate, granularityMonths, periodStartMonth),
        periodStartDate: periodDate.toISOString(),
        expectedFailures: Number(periodFailures.toFixed(4)),
        cumulativeFailures: Number(cumulativeTotal.toFixed(4)),
        populationSize: turbines.length,
        granularityMonths
      };

      if (useMonteCarloSimulation) {
        const confidenceWidth = CONFIDENCE_INTERVAL_FACTOR;
        result.lowerBound = Number((periodFailures * (1 - confidenceWidth)).toFixed(4));
        result.upperBound = Number((periodFailures * (1 + confidenceWidth)).toFixed(4));
        result.cumulativeLowerBound = Number((cumulativeTotal * (1 - confidenceWidth)).toFixed(4));
        result.cumulativeUpperBound = Number((cumulativeTotal * (1 + confidenceWidth)).toFixed(4));
      }

      results.push(result);
    }

    return results;
  }
}

/**
 * Check if MLE result has valid parameter uncertainty information
 */
export function hasValidParameterUncertainty(mleResult?: MLEResult | null): boolean {
  if (!mleResult) return false;
  
  return Boolean(
    mleResult.fisherInformation &&
    mleResult.fisherInformation.determinant > 0 &&
    mleResult.standardErrors &&
    mleResult.standardErrors.shape > 0 &&
    mleResult.standardErrors.scale > 0
  );
}

/**
 * Calculate forecast with automatic method selection based on available data
 */
export async function calculateForecast(
  turbines: TurbineForForecastData[],
  weibullParams: WeibullParameters,
  mleResult?: MLEResult | null,
  options: {
    horizonYears?: number;
    granularityMonths?: number;
    forceMonteCarloMethod?: boolean;
    preferParameterUncertainty?: boolean;
  } = {}
): Promise<{
  results: ForecastResult[];
  method: 'parametric-bootstrap' | 'monte-carlo' | 'traditional';
  methodDetails: string;
}> {
  
  const {
    horizonYears = 5,
    granularityMonths = 12,
    forceMonteCarloMethod = false,
    preferParameterUncertainty = true
  } = options;
  
  // Check if we can and should use parameter uncertainty
  const canUseParameterUncertainty = hasValidParameterUncertainty(mleResult);
  const shouldUseParameterUncertainty = preferParameterUncertainty && !forceMonteCarloMethod;
  
  let results: ForecastResult[];
  let method: 'parametric-bootstrap' | 'monte-carlo' | 'traditional';
  let methodDetails: string;
  
  if (canUseParameterUncertainty && shouldUseParameterUncertainty && mleResult) {
    // Use parametric bootstrap with parameter uncertainty
    results = await SimpleWeibullForecastEngine.calculateForecast(
      turbines,
      weibullParams,
      horizonYears,
      granularityMonths,
      true, // useMonteCarloSimulation
      undefined, // monteCarloOptions
      mleResult // Pass MLE result
    );
    
    method = 'parametric-bootstrap';
    methodDetails = `Parametric bootstrap with ${mleResult.confidenceIntervals?.method || 'Fisher'} uncertainty`;
    
  } else if (!forceMonteCarloMethod && turbines.length < 20) {
    // For small populations, use traditional method
    results = await SimpleWeibullForecastEngine.calculateForecast(
      turbines,
      weibullParams,
      horizonYears,
      granularityMonths,
      false // Don't use Monte Carlo
    );
    
    method = 'traditional';
    methodDetails = 'Deterministic calculation (small population)';
    
  } else {
    // Use traditional Monte Carlo
    results = await SimpleWeibullForecastEngine.calculateForecast(
      turbines,
      weibullParams,
      horizonYears,
      granularityMonths,
      true, // useMonteCarloSimulation
      {
        minSimulations: 2000,
        maxSimulations: 50000,
        convergenceThreshold: 0.1
      }
    );
    
    method = 'monte-carlo';
    methodDetails = 'Monte Carlo with fixed parameters';
    
    if (canUseParameterUncertainty && forceMonteCarloMethod) {
      methodDetails += ' (parameter uncertainty available but not used)';
    }
  }
  
  return {
    results,
    method,
    methodDetails
  };
}

/**
 * Get a summary of uncertainty sources in the forecast
 */
export function getUncertaintySources(
  method: 'parametric-bootstrap' | 'monte-carlo' | 'traditional',
  _mleResult?: MLEResult | null
): {
  parameterUncertainty: boolean;
  aleatoryUncertainty: boolean;
  description: string;
} {
  
  switch (method) {
    case 'parametric-bootstrap':
      return {
        parameterUncertainty: true,
        aleatoryUncertainty: true,
        description: 'Both parameter uncertainty (from MLE) and random variation (Poisson) included'
      };
      
    case 'monte-carlo':
      return {
        parameterUncertainty: false,
        aleatoryUncertainty: true,
        description: 'Only random variation included (parameters assumed known exactly)'
      };
      
    case 'traditional':
      return {
        parameterUncertainty: false,
        aleatoryUncertainty: false,
        description: 'Deterministic forecast without uncertainty quantification'
      };
      
    default:
      return {
        parameterUncertainty: false,
        aleatoryUncertainty: false,
        description: 'Unknown method'
      };
  }
}

/**
 * Format confidence interval width for display
 */
export function formatConfidenceIntervalWidth(
  lowerBound: number,
  upperBound: number,
  expected: number
): string {
  const width = upperBound - lowerBound;
  const relativeWidth = expected > 0 ? (width / expected) * 100 : 0;
  
  if (relativeWidth > 200) {
    return 'Very Wide (>200% of estimate)';
  } else if (relativeWidth > 100) {
    return 'Wide (100-200% of estimate)';
  } else if (relativeWidth > 50) {
    return 'Moderate (50-100% of estimate)';
  } else if (relativeWidth > 20) {
    return 'Narrow (20-50% of estimate)';
  } else {
    return 'Very Narrow (<20% of estimate)';
  }
}

/**
 * Create a diagnostic summary of the forecast method and quality
 */
export function createForecastDiagnostics(
  results: ForecastResult[],
  method: 'parametric-bootstrap' | 'monte-carlo' | 'traditional',
  mleResult?: MLEResult | null
): {
  method: string;
  uncertaintySources: ReturnType<typeof getUncertaintySources>;
  confidenceIntervalQuality: string;
  warnings: string[];
  recommendations: string[];
} {
  
  const uncertaintySources = getUncertaintySources(method, mleResult);
  const warnings: string[] = [];
  const recommendations: string[] = [];
  
  // Check confidence interval quality
  let ciQuality = 'Not Available';
  if (results.length > 0 && results[0].lowerBound !== undefined) {
    const firstYear = results[0];
    const lastYear = results[results.length - 1];
    
    const firstYearWidth = formatConfidenceIntervalWidth(
      firstYear.lowerBound || 0,
      firstYear.upperBound || 0,
      firstYear.expectedFailures
    );
    
    const lastYearWidth = formatConfidenceIntervalWidth(
      lastYear.cumulativeLowerBound || 0,
      lastYear.cumulativeUpperBound || 0,
      lastYear.cumulativeFailures
    );
    
    ciQuality = `Year 1: ${firstYearWidth}, Year 5: ${lastYearWidth}`;
    
    // Add warnings for poor CI quality
    if (firstYearWidth.includes('Very Wide') || lastYearWidth.includes('Very Wide')) {
      warnings.push('Confidence intervals are very wide, indicating high uncertainty');
      
      if (method === 'monte-carlo') {
        recommendations.push('Consider using MLE with parameter uncertainty for better CI');
      }
    }
  }
  
  // Check if parameter uncertainty should be used
  if (method !== 'parametric-bootstrap' && hasValidParameterUncertainty(mleResult)) {
    recommendations.push('MLE parameter uncertainty is available - consider using it for more accurate CI');
  }
  
  // Check convergence for Monte Carlo
  if (method === 'monte-carlo' && results.length > 0) {
    const converged = results[0].convergenceAchieved;
    if (!converged) {
      warnings.push('Monte Carlo simulation did not converge');
      recommendations.push('Increase maximum simulations or relax convergence threshold');
    }
  }
  
  return {
    method: `${method} (${uncertaintySources.description})`,
    uncertaintySources,
    confidenceIntervalQuality: ciQuality,
    warnings,
    recommendations
  };
}
